import { z } from 'zod';
import {
  phoneValidator,
  birthDateValidator,
  countryValidator,
  SalutationEnum,
  nationalityValidator,
  calculateAge,
  ADULT_MIN_AGE,
} from './validation';
import { parseGermanDateLocal } from '@/utils/formatting/dateParser';

/**
 * Contact Person Base Schema
 * Core contact/invoice recipient fields without traveler-specific data.
 * Used for validation when contact person is NOT traveling (accompany mode).
 */
export const ContactPersonBaseSchema = z.object({
  firstName: z
    .string()
    .min(1, 'ibe.validation.firstName.required')
    .max(100, 'ibe.validation.firstName.too-long'),
  lastName: z
    .string()
    .min(1, 'ibe.validation.lastName.required')
    .max(100, 'ibe.validation.lastName.too-long'),
  street: z
    .string()
    .min(1, 'ibe.validation.street.required')
    .max(200, 'ibe.validation.street.too-long'),
  postalCode: z
    .string()
    .min(1, 'ibe.validation.postalCode.required')
    .regex(/^\d{4,10}$/, 'ibe.validation.postalCode.invalid'),
  location: z
    .string()
    .min(1, 'ibe.validation.location.required')
    .max(100, 'ibe.validation.location.too-long'),
  country: countryValidator,
  mobile: phoneValidator,
  email: z.string().min(1, 'ibe.validation.email.required').email('ibe.validation.email.invalid'),
});

/**
 * Contact Person Traveling Schema
 * Extends base with salutation and birthDate (18+), required when contact person
 * is also traveling (solo mode). These fields are needed for DaVinci Participants.
 */
export const ContactPersonTravelingSchema = ContactPersonBaseSchema.extend({
  salutation: SalutationEnum,
  birthDate: birthDateValidator.refine((date) => {
    const age = calculateAge(parseGermanDateLocal(date));
    return age >= ADULT_MIN_AGE;
  }, 'ibe.validation.contact.age-minimum'),
  nationality: nationalityValidator,
});

/**
 * Contact Person Schema (unified type with optional traveler fields)
 * Used for data storage and type definitions throughout the app.
 * salutation and birthDate are optional - present only in solo mode.
 */
export const ContactPersonSchema = ContactPersonBaseSchema.extend({
  salutation: SalutationEnum.optional(),
  birthDate: birthDateValidator.optional(),
  nationality: nationalityValidator.optional(),
});

export type ContactPerson = z.infer<typeof ContactPersonSchema>;
